import { SHBox, SHStack, SHTableCell } from "@components/design-systems";
import { ArrowDownSVG } from "@components/svgs/arrow-down";
import { FeatureAnalysisRowDTO } from "@models/platform-analysis/entities/steps/feature";
import { FeatureAnalysisRowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { SxProps, Theme, useTheme } from "@mui/material";
import { setCollapseStateChangedManually } from "@redux/slices/platform-analysis";
import { useAppDispatch } from "@redux/store";
import { Cell, flexRender } from "@tanstack/react-table";
import { memo, useCallback, useMemo } from "react";
import { TableVirtualizationConfigs } from "../../../configs";

type AnalysisTableStickyCellProps = {
  cell: Cell<FeatureAnalysisRowDTO, unknown>;
  isRowExpanded?: boolean;
};

export const AnalysisTableStickyCell = memo(
  ({ cell, isRowExpanded }: AnalysisTableStickyCellProps) => {
    const { palette } = useTheme();
    const dispatch = useAppDispatch();

    const { column, row } = cell;
    const { level } = row.original;
    const isFeatureLevel = level === FeatureAnalysisRowLevel.Feature;

    const cellContent = useMemo(
      () => flexRender(column.columnDef.cell, cell.getContext()),
      [column.columnDef.cell, cell],
    );

    const handleClick = useCallback(() => {
      if (isFeatureLevel) return;
      row.toggleExpanded();
      dispatch(setCollapseStateChangedManually(true));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFeatureLevel, row]);

    const cellStyles: SxProps<Theme> = useMemo(
      () => ({
        position: "sticky",
        display: "flex",
        left: column.getStart(),
        width: column.getSize(),
        flexShrink: 0,
        minHeight: TableVirtualizationConfigs.DefaultRowHeight,
        height: "auto",
        alignItems: "center",
        padding: 0,
        opacity: 1,
        zIndex: 2,
        cursor: isFeatureLevel ? "default" : "pointer",
      }),
      [column, isFeatureLevel],
    );

    const borderColor = useMemo(
      () => `1px solid ${palette.secondary[100]}`,
      [palette.secondary],
    );

    const arrowRotation = useMemo(
      () => `rotate(${isRowExpanded ? 0 : 180}deg)`,
      [isRowExpanded],
    );

    return (
      <SHTableCell
        className={"FeatureAnalysisTitleSHTableCell"}
        sx={cellStyles}
        onClick={handleClick}
      >
        <SHStack
          className={`FeatureAnalysisTitleCell FeatureAnalysisTitleCell-${level}`}
          direction="row"
          width="100%"
          height="100%"
          bgcolor="white"
          border={borderColor}
          borderBottom="none"
          paddingX={1.5}
          paddingY={0.5}
        >
          {cellContent}
          {!isFeatureLevel && (
            <SHBox
              className={"FeatureAnalysisTitleCell-arrow"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <ArrowDownSVG
                width={16}
                height={16}
                style={{
                  transition: "all ease-in-out 0.2s",
                  transform: arrowRotation,
                }}
              />
            </SHBox>
          )}
        </SHStack>
      </SHTableCell>
    );
  },
);
